// Converts the React Flow builder graph (nodes/edges) to and from the server's
// automation-flow contract.
//
// SERVER SHAPES
//   FlowRequest  = { name, triggerKind, eventType?, graph: FlowGraph, enabled }
//   FlowGraph    = { nodes: [FlowNode], edges: [FlowEdge] }
//   FlowNode     = { key, kind, config: { inputs: {[k]: {kind, value}}, options: {} } }
//   FlowEdge     = { from, to }         // reference FlowNode.key
//   FlowResponse = { id, name, triggerKind, eventType, graph, enabled, ... }
//
// KEY INVARIANT — the FlowNode `key` MUST equal the builder's local output-path
// prefix (`prefixFor`), because a mapping like {{createTrainee_n5.traineeId}} is
// resolved server-side by matching the first path segment against a node key. So the
// key we send is exactly the prefix the mapping UI already used. The Start node is
// dropped (it's a pure UI origin); the trigger node becomes a TRIGGER-kind FlowNode.

// A stable per-node key/prefix — mirrors AutomationFlowPage.prefixFor EXACTLY.
// (Kept here so the serializer is self-contained; must stay in sync.)
export function nodeKey(node) {
    if (node.type === "trigger") {
        return node.data?.trigger?.source === "ARBOX" ? "arbox" : "trigger";
    }
    const kind = node.data?.action?.kind || node.data?.brick?.kind || "step";
    return `${kind.toLowerCase()}_${node.id}`;
}

// The brick kind a node represents.
function nodeKind(node) {
    if (node.type === "trigger") return node.data?.trigger?.kind;
    return node.data?.action?.kind || node.data?.brick?.kind;
}

// Map one UI mapping value {type:"FIELD"|"STATIC"|"HTML", value} → server {kind, value}.
// HTML (rich-text body) is a STATIC literal server-side.
function toBinding(m) {
    if (!m || m.value === undefined || m.value === null || m.value === "") return null;
    const kind = m.type === "FIELD" ? "FIELD" : "STATIC";
    return {kind, value: m.value};
}

// Map a server binding {kind, value} → UI mapping {type, value}.
// A rich-text target (e.g. the email body) must come back as type "HTML" — otherwise the rich-text
// editor, which only renders when type === "HTML", shows empty even though the value is present.
function fromBinding(b, richText = false) {
    if (!b) return null;
    if (b.kind === "FIELD") return {type: "FIELD", value: b.value};
    return {type: richText ? "HTML" : "STATIC", value: b.value};
}

// Build the per-node config the server stores: bindings + options (+ the data
// brick's formula, folded into options so the whole node config round-trips).
function nodeConfig(node) {
    const inputs = {};
    const mapping = node.data?.mapping || {};
    Object.entries(mapping).forEach(([k, m]) => {
        const b = toBinding(m);
        if (b) inputs[k] = b;
    });

    const options = {...(node.data?.options || {})};
    // Filter brick condition lives in node.data.condition → options for the server FilterBrick.
    if (node.data?.condition) {
        const c = node.data.condition;
        if (c.field) inputs.left = {kind: "FIELD", value: `{{${c.field}}}`};
        if (c.operator) options.operator = c.operator;
        if (c.value !== undefined) options.right = c.value;
    }
    // Data-transform formula (outputName + expression) round-trips through options.
    if (node.data?.formula) {
        options.outputName = node.data.formula.outputName;
        options.expression = node.data.formula.expression;
    }
    // HTTP headers round-trip through options.headers (a {key: value} map).
    if (node.data?.headers && Object.keys(node.data.headers).length) {
        options.headers = node.data.headers;
    }
    return {inputs, options};
}

/**
 * Serialize the builder graph into a FlowRequest.
 * @returns {{ok: boolean, error?: string, request?: object}}
 */
export function serializeFlow(nodes, edges, {name, enabled}) {
    const trigger = nodes.find(n => n.type === "trigger");
    if (!trigger || !trigger.data?.trigger) {
        return {ok: false, error: "יש לבחור טריגר לפני שמירה"};
    }
    const triggerSpec = trigger.data.trigger;

    // Real (non-start) nodes → FlowNode[]. Start is a UI-only origin, dropped.
    // position is carried through so the exact canvas layout (parallel branches!) survives a reload.
    const real = nodes.filter(n => n.type !== "start");
    const flowNodes = real.map(n => ({
        key: nodeKey(n),
        kind: nodeKind(n),
        config: nodeConfig(n),
        position: n.position ? {x: n.position.x, y: n.position.y} : undefined,
    }));

    // Edges between real nodes → FlowEdge[] (start→trigger edge is dropped with start).
    const keyById = new Map(real.map(n => [n.id, nodeKey(n)]));
    const flowEdges = edges
        .filter(e => keyById.has(e.source) && keyById.has(e.target))
        .map(e => ({from: keyById.get(e.source), to: keyById.get(e.target)}));

    const request = {
        name: name || "אוטומציה חדשה",
        triggerKind: triggerSpec.kind,
        eventType: triggerSpec.kind === "INTERNAL_EVENT" ? triggerSpec.eventType : null,
        graph: {nodes: flowNodes, edges: flowEdges},
        enabled: Boolean(enabled),
        // Links a PULL flow to its connected integration (Arbox). Set on the trigger node by the
        // "connect" action; null for internal-event / webhook triggers. The raw API key is NEVER
        // sent — it lives encrypted server-side, keyed by this integrationId.
        integrationId: trigger.data.integrationId || null,
    };
    return {ok: true, request};
}

/**
 * Rebuild builder {nodes, edges} from a FlowResponse for edit mode. Needs the
 * trigger/action/data catalog lookups (server-driven) so each node's spec is
 * re-attached for the config panel.
 *
 * @param flow           the FlowResponse
 * @param resolvers      { triggerByKind, actionByKind, dataByKind }
 */
export function deserializeFlow(flow, {triggerByKind, actionByKind, dataByKind}, BRAND) {
    const graph = flow.graph || {nodes: [], edges: []};
    const START = {id: "start", type: "start", position: {x: 60, y: 180}, data: {}, deletable: false, draggable: false};

    // Map server FlowNode.key → a builder node. The trigger keeps the id "trigger".
    const nodes = [START];
    const idByKey = new Map();
    let seq = 1;
    let fallbackX = 480;   // only used for legacy flows saved before positions were persisted

    // A node is the trigger if its kind resolves to a trigger spec — NOT by a hardcoded key. The
    // trigger's key can be any namespace (trainee / arbox / webhook), so keying off the kind is what
    // makes migrated + all future flows load correctly.
    const isTriggerNode = (fn) => Boolean(triggerByKind(fn.kind, fn.config?.options?.eventType || flow.eventType));

    graph.nodes.forEach(fn => {
        const isTrigger = isTriggerNode(fn);
        const id = isTrigger ? "trigger" : `ld${seq++}`;
        idByKey.set(fn.key, id);

        // Restore the exact saved canvas position so parallel branches keep their layout.
        // Fall back to a simple line only for old flows that predate persisted positions.
        let position;
        if (fn.position && typeof fn.position.x === "number") {
            position = {x: fn.position.x, y: fn.position.y};
        } else if (isTrigger) {
            position = {x: 260, y: 180};
        } else {
            position = {x: fallbackX, y: 180};
            fallbackX += 220;
        }

        let data;
        let type;
        if (isTrigger) {
            const spec = triggerByKind(fn.kind, fn.config?.options?.eventType || flow.eventType);
            // Restore the integration link for PULL triggers so the panel shows "connected" on reopen.
            // discoveredFields aren't persisted in the graph — the panel re-fetches them via the
            // integration's fields endpoint when needed; the static spec.outputs is the fallback.
            data = {trigger: spec, keyOk: Boolean(flow.integrationId), integrationId: flow.integrationId || undefined};
            type = "trigger";
        } else {
            const actionSpec = actionByKind(fn.kind);
            const dataSpec = !actionSpec && dataByKind ? dataByKind(fn.kind) : null;
            const spec = actionSpec || dataSpec;
            type = dataSpec ? "data" : "action";
            data = rebuildNodeData(fn, spec, type);
        }
        nodes.push({id, type, position, data});
    });

    // Edges: server uses keys; builder uses ids. Always keep start→trigger.
    const edges = [{
        id: "e-start-trigger", source: "start", target: "trigger",
        animated: true, style: {stroke: BRAND},
    }];
    graph.edges.forEach(fe => {
        const source = idByKey.get(fe.from);
        const target = idByKey.get(fe.to);
        if (source && target) {
            edges.push({id: `e-${source}-${target}`, source, target, animated: true, style: {stroke: BRAND}});
        }
    });

    return {nodes, edges};
}

// Turn a server FlowNode's config back into builder node.data (mapping/options/formula/condition).
function rebuildNodeData(fn, spec, type) {
    const config = fn.config || {};
    // Which inputs are rich-text (e.g. email body) — so their value comes back as an HTML mapping.
    const richTextKeys = new Set((spec?.targets || []).filter(t => t.richText).map(t => t.key));
    const mapping = {};
    Object.entries(config.inputs || {}).forEach(([k, b]) => {
        const m = fromBinding(b, richTextKeys.has(k));
        if (m) mapping[k] = m;
    });
    const data = {action: type === "action" ? spec : undefined, brick: type === "data" ? spec : undefined, mapping};
    if (spec?.formula && config.options) {
        data.formula = {outputName: config.options.outputName, expression: config.options.expression};
    }
    if (spec?.filter && config.options) {
        data.condition = {
            field: config.inputs?.left?.value?.replace(/\{\{\s*|\s*}}/g, "") || "",
            operator: config.options.operator,
            value: config.options.right,
        };
    }
    // Non-formula/filter options (e.g. sendWelcomeEmail, http method) live in data.options.
    if (config.options && !spec?.formula && !spec?.filter) {
        data.options = config.options;
    }
    // HTTP headers were stored under options.headers → restore to data.headers.
    if (config.options?.headers) {
        data.headers = config.options.headers;
    }
    return data;
}
